import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import BackgroundBlobs from '../components/BackgroundBlobs';
import Header from '../components/Header';
import TotalPeopleSolved from '../components/TotalPeopleSolved';
import PreviousWinners from '../components/PreviousWinners';
import PuzzleOverlay from '../components/PuzzleOverlay';
import ProfileModal from '../components/ProfileModal';
import CreditsModal from '../components/CreditsModal';
import ReferEarnModal from '../components/ReferEarnModal';
import SettingsModal from '../components/SettingsModal';
import RedeemRewardPopup from '../components/RedeemRewardPopup';
import WinnerAnnouncementPopup from '../components/WinnerAnnouncementPopup';
import TicketPopup from '../components/TicketPopup';
import { ExternalLink } from 'lucide-react';
import LegalLinks from '../components/LegalLinks';
import { APP_DOWNLOAD_URL } from '../lib/appDownload';
import { api } from '../lib/api';

const TELEGRAM_CHANNEL_URL = 'https://t.me/fiftykclub';

const formatReferralEarningsBadge = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return '';

  const rounded = Math.round(amount * 100) / 100;
  return Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toFixed(2).replace(/\.?0+$/, '');
};

const Dashboard = () => {
  const {
    isAuthenticated,
    hasSolvedToday,
    loading,
    user,
    getReferralLink,
    poolTotal,
    winnerAnnouncement,
    isWinnerPopupOpen,
    markWinnerAnnouncementSeenById,
    closeWinnerAnnouncement,
    adminConfig,
  } = useAuth();
  const navigate = useNavigate();
  const [showPuzzle, setShowPuzzle] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showCredits, setShowCredits] = useState(false);
  const [showWithdrawal, setShowWithdrawal] = useState(false); // Reused name; now opens Redeem popup
  const [showRefer, setShowRefer] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);
  const [hasShownCelebration, setHasShownCelebration] = useState(false);
  const [referralCopied, setReferralCopied] = useState(false);
  const [freePuzzleMessage, setFreePuzzleMessage] = useState('');
  const [isStartingFreePuzzle, setIsStartingFreePuzzle] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [showTickets, setShowTickets] = useState(false);
  const referralLink = getReferralLink();

  // Current-cycle tickets come from the backend (source of truth) on the user
  // object; the frontend never calculates ticket numbers itself.
  const currentCycleTickets = Array.isArray(user?.currentCycleTickets) ? user.currentCycleTickets : [];
  const ticketCount = currentCycleTickets.length;


  // Check if user is authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, loading, navigate]);

  useEffect(() => {
    if (!user?.id) return undefined;

    let isMounted = true;

    const loadUnreadMessages = async () => {
      try {
        const { data } = await api.getUnreadMessageCount(user.id);
        if (isMounted) setUnreadMessages(Math.max(0, Number(data?.unreadCount) || 0));
      } catch (error) {
        try {
          const { data } = await api.getChats(user.id);
          if (isMounted) setUnreadMessages(Math.max(0, Number(data?.unreadCount) || 0));
        } catch {
          if (isMounted) setUnreadMessages(0);
        }
      }
    };

    loadUnreadMessages();
    const interval = setInterval(loadUnreadMessages, 15000);
    const handleVisibilityChange = () => {
      if (!document.hidden) loadUnreadMessages();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMounted = false;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.id]);

  // Daily puzzle lock flow. Automatic top-up / earn-credits popups are intentionally disabled on dashboard load.
  useEffect(() => {
    if (loading || !isAuthenticated || !user) return;

    const hasDirectPuzzleAccess = Number(user?.credits || 0) > 0;

    if (!hasSolvedToday && hasDirectPuzzleAccess) {
      setShowPuzzle(true);
      return;
    }

    setShowPuzzle(false);
  }, [isAuthenticated, hasSolvedToday, loading, user]);

  // Celebrate when winner popup appears
  useEffect(() => {
    if (!isWinnerPopupOpen || hasShownCelebration) return;

    triggerCelebration();
    setHasShownCelebration(true);

    const timer = setTimeout(() => setHasShownCelebration(false), 4000);
    return () => clearTimeout(timer);
  }, [isWinnerPopupOpen, hasShownCelebration]);

  const triggerCelebration = () => {
    const duration = 5 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 200 };

    const randomInRange = (min, max) => Math.random() * (max - min) + min;

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) return clearInterval(interval);

      const particleCount = 50 * (timeLeft / duration);
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
    }, 250);
  };

  const handleUserClick = async (selectedProfile) => {
    setSelectedUserProfile(selectedProfile);
    setShowProfile(true);

    const selectedUserId = selectedProfile?.userId || selectedProfile?.id;
    if (!selectedUserId) return;

    try {
      const { data } = await api.getUser(selectedUserId);
      if (data?.user) {
        setSelectedUserProfile(data.user);
      }
    } catch (error) {
      // Keep the already-open snapshot if the live profile fetch fails.
      console.warn('Unable to refresh selected public profile', error?.response?.data?.detail || error.message);
    }
  };

  const handleCopyReferralLink = async () => {
    if (!referralLink) return;

    try {
      await navigator.clipboard.writeText(referralLink);
      setReferralCopied(true);
      setTimeout(() => setReferralCopied(false), 2000);
    } catch (error) {
      setReferralCopied(false);
    }
  };

  const handleStartFreePuzzle = async () => {
    if (!user?.id || isStartingFreePuzzle) return;

    console.info('HARDAY_SPONSOR_UNLOCK_CREATED');
    setFreePuzzleMessage('');

    if (!nextPuzzleEntryNumber || dailyEntriesCompleted >= 3) {
      setFreePuzzleMessage('You have completed all 3 puzzle entries for today.');
      return;
    }

    if (!currentFreePuzzleSponsorLink) {
      console.info('free puzzle sponsor link missing');
      setFreePuzzleMessage('Free puzzle unlock is not available right now.');
      return;
    }

    try {
      setIsStartingFreePuzzle(true);
      const { data } = await api.createFreePuzzleUnlockSession(user.id, nextPuzzleEntryNumber);
      const sponsorLink = data?.sponsorLink;
      if (!sponsorLink) {
        console.info('free puzzle sponsor link missing');
        setFreePuzzleMessage('Free puzzle unlock is not available right now.');
        return;
      }
      console.info('HARDAY_SPONSOR_UNLOCK_CREATED');
      window.location.href = sponsorLink;
    } catch (error) {
      setFreePuzzleMessage(error?.response?.data?.detail || error.message || 'Unable to start free puzzle unlock.');
    } finally {
      setIsStartingFreePuzzle(false);
    }
  };


  if (loading || !isAuthenticated) {
    return null;
  }

  const isFreePuzzleUser = Number(user?.credits || 0) <= 0;
  const dailyEntriesCompleted = Number(user?.dailyPuzzleEntriesCompleted || 0);
  const nextPuzzleEntryNumber = user?.nextPuzzleEntryNumber || (dailyEntriesCompleted < 3 ? dailyEntriesCompleted + 1 : null);
  const freePuzzleSponsorLinks = Array.isArray(adminConfig?.freePuzzleSponsorLinks) ? adminConfig.freePuzzleSponsorLinks : [adminConfig?.freePuzzleSponsorLink];
  const currentFreePuzzleSponsorLink = nextPuzzleEntryNumber ? freePuzzleSponsorLinks[nextPuzzleEntryNumber - 1] : '';
  const freePuzzleButtonLabels = { 1: 'Solve Puzzle', 2: 'Solve 2nd Puzzle', 3: 'Solve 3rd Puzzle' };
  const freePuzzleButtonLabel = freePuzzleButtonLabels[nextPuzzleEntryNumber] || 'All puzzles completed';
  const referralEarningsBadge = formatReferralEarningsBadge(
    user?.redeemableAmount ?? user?.rewardProgressBalance ?? user?.referralCountProgress ?? user?.referralEarningsCredits,
  );

  return (
    <div className="w-full max-w-md mx-auto min-h-screen relative overflow-hidden bg-white shadow-2xl">
      <BackgroundBlobs />

      <div className="relative z-10">
        {/* Header */}
        <Header
          onCreditsClick={() => setShowCredits(true)}
          onMessagesClick={() => navigate('/messages')}
          onTicketClick={() => setShowTickets(true)}
          ticketCount={ticketCount}
          unreadMessages={unreadMessages}
          onProfileClick={() => {
            setSelectedUserProfile(null);
            setShowProfile(true);
          }}
        />
        <TicketPopup
          isOpen={showTickets}
          tickets={currentCycleTickets}
          onClose={() => setShowTickets(false)}
        />

        {/* Main Content */}
        <div className="p-5 space-y-6 pb-8">
          {/* Total People Solved - Pool */}
          <TotalPeopleSolved count={poolTotal} />

          {isFreePuzzleUser && dailyEntriesCompleted < 3 && (
            <motion.div
              className="-mt-3 space-y-2"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 24, delay: 0.28 }}
              data-testid="free-puzzle-unlock-card"
            >
              <button
                type="button"
                onClick={handleStartFreePuzzle}
                disabled={isStartingFreePuzzle}
                data-testid="free-puzzle-solve-btn"
                className="w-full py-3 rounded-full bg-gradient-to-r from-[#9333EA] to-[#EC4899] text-white font-semibold disabled:opacity-60 flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                {isStartingFreePuzzle ? 'Opening sponsor...' : freePuzzleButtonLabel}
              </button>
              {freePuzzleMessage && (
                <p className="text-xs text-center font-semibold text-[#9333EA] font-['DM_Sans']">{freePuzzleMessage}</p>
              )}
            </motion.div>
          )}

          {/* Previous Winners */}
          <PreviousWinners onUserClick={handleUserClick} />

          {/* Action list — minimal text-only menu (no cards, buttons, icons or
              background shapes). Text styled to match the "Harday" wordmark and
              the total-solved number: Outfit font with the brand gradient
              (.text-gradient), one item per line with generous tap targets. */}
          <motion.nav
            className="w-full flex flex-col -mt-2 font-['Outfit']"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 24, delay: 0.35 }}
            data-testid="dashboard-action-list"
          >
            {/* 1. Refer & Earn */}
            <button
              type="button"
              onClick={() => setShowRefer(true)}
              data-testid="refer-earn-btn"
              className="w-full min-h-[44px] py-1.5 flex items-center text-left text-[17px] font-medium text-[#1F2937] active:opacity-60"
            >
              <span className="mr-3 text-[#B0B0B0]" aria-hidden="true">&bull;</span>
              <span className="text-gradient">Refer &amp; Earn</span>
            </button>

            {/* 2. Settings */}
            <button
              type="button"
              onClick={() => setShowSettings(true)}
              data-testid="settings-btn"
              className="w-full min-h-[44px] py-1.5 flex items-center text-left text-[17px] font-medium text-[#1F2937] active:opacity-60"
            >
              <span className="mr-3 text-[#B0B0B0]" aria-hidden="true">&bull;</span>
              <span className="text-gradient">Settings</span>
            </button>

            {/* 3. Join Telegram — unread count kept as subtle inline text */}
            <a
              href={TELEGRAM_CHANNEL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="telegram-group-btn"
              className="w-full min-h-[44px] py-1.5 flex items-center text-left text-[17px] font-medium text-[#1F2937] active:opacity-60"
            >
              <span className="mr-3 text-[#B0B0B0]" aria-hidden="true">&bull;</span>
              <span className="text-gradient">Join Telegram</span>
              {referralEarningsBadge && (
                <span
                  data-testid="telegram-referral-earnings-badge"
                  aria-label={`${referralEarningsBadge} unread`}
                  className="ml-1.5 text-[14px] text-[#9CA3AF]"
                >
                  ({referralEarningsBadge})
                </span>
              )}
            </a>

            {/* 4. Copy referral link — text only; copies the same link and shows
                "Copied" for ~2s. The URL itself is no longer displayed. */}
            <button
              type="button"
              onClick={handleCopyReferralLink}
              data-testid="dashboard-copy-referral-link"
              aria-label="Copy your referral link"
              className="w-full min-h-[44px] py-1.5 flex items-center text-left text-[17px] font-medium text-[#1F2937] active:opacity-60"
            >
              <span className="mr-3 text-[#B0B0B0]" aria-hidden="true">&bull;</span>
              <span className="text-gradient">{referralCopied ? 'Copied' : 'Copy your referral link'}</span>
            </button>

            {/* 5. Download Harday App — direct APK download */}
            <a
              href={APP_DOWNLOAD_URL}
              download=""
              data-testid="dashboard-download-android-app"
              className="w-full min-h-[44px] py-1.5 flex items-center text-left text-[17px] font-medium text-[#1F2937] active:opacity-60"
            >
              <span className="mr-3 text-[#B0B0B0]" aria-hidden="true">&bull;</span>
              <span className="text-gradient">Download Harday App</span>
            </a>
          </motion.nav>

          {/* Footer with legal links */}
          <footer className="pt-4 border-t border-gray-100" data-testid="dashboard-footer">
            <LegalLinks align="center" tone="muted" />
            <p className="text-[11px] text-center text-[#9CA3AF] mt-2 font-['DM_Sans']">
              &copy; {new Date().getFullYear()} Kings Wake Digital Private Limited
            </p>
          </footer>
        </div>
      </div>

      {/* Modals & Overlays */}
      <PuzzleOverlay isOpen={showPuzzle} onClose={() => setShowPuzzle(false)} isForced={!hasSolvedToday} />
      <ProfileModal
        isOpen={showProfile}
        onClose={() => {
          setShowProfile(false);
          setSelectedUserProfile(null);
        }}
        profileData={selectedUserProfile}
        onTopUp={() => setShowCredits(true)}
      />
      <CreditsModal isOpen={showCredits} onClose={() => setShowCredits(false)} />
      <ReferEarnModal
        isOpen={showRefer}
        onClose={() => setShowRefer(false)}
        onWithdraw={() => setShowWithdrawal(true)}
        onOpenProfile={(referral) => {
          setShowRefer(false);
          handleUserClick(referral);
        }}
      />
      <SettingsModal isOpen={showSettings} onClose={() => setShowSettings(false)} />
      <RedeemRewardPopup
        isOpen={showWithdrawal}
        onClose={() => setShowWithdrawal(false)}
      />
      <WinnerAnnouncementPopup
        isOpen={isWinnerPopupOpen}
        winner={winnerAnnouncement}
        onWinnerSeen={markWinnerAnnouncementSeenById}
        onClose={closeWinnerAnnouncement}
      />
    </div>
  );
};

export default Dashboard;
